import React, { useState, useCallback } from 'react';
import {
    Button,
    Card,
    Group,
    Text,
    TextInput,
    ActionIcon,
    Stack,
    Alert,
    Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
    IconTrash,
    IconPlus,
    IconFolderOpen,
    IconChevronUp,
    IconChevronDown as IconChevronDownMove,
} from '@tabler/icons-react';
import { t } from 'i18next';
import { modals } from '@mantine/modals';
import { IJournalCategory, IJournalQuestion } from '../../../global/entities/Journal/Journal.ts';
import {
    useAddCategory,
    useUpdateCategory,
    useDeleteCategory,
    useReorderCategories
} from '../../../global/entities/Journal/queries/useJournals.ts';
import { JournalQuestionManager } from './JournalQuestionManager.tsx';

interface IJournalCategoryManagerProps {
    categories: IJournalCategory[];
    onCategoriesChange: (categories: IJournalCategory[]) => void;
    journalId?: number;
}

export const JournalCategoryManager: React.FC<IJournalCategoryManagerProps> = ({
    categories,
    onCategoriesChange,
    journalId,
}) => {
    const [showAddForm, setShowAddForm] = useState(false);

    // Backend mutations
    const addCategoryMutation = useAddCategory();
    const updateCategoryMutation = useUpdateCategory();
    const deleteCategoryMutation = useDeleteCategory();
    const reorderCategoriesMutation = useReorderCategories();

    const newCategoryForm = useForm({
        initialValues: {
            nameFr: '',
            nameEn: '',
        },
        validate: {
            nameFr: (value) => (!value.trim() ? t('formError.requiredNameFr') : null),
            nameEn: (value) => (!value.trim() ? t('formError.requiredNameEn') : null),
        },
    });

    const handleMoveCategory = useCallback(async (fromIndex: number, toIndex: number) => {
        if (fromIndex === toIndex || toIndex < 0 || toIndex >= categories.length) 
            return;

        const newCategories = [...categories];
        const categoryToMove = newCategories[fromIndex];
        
        // Remove category from current position
        newCategories.splice(fromIndex, 1);
        
        // Insert at new position
        newCategories.splice(toIndex, 0, categoryToMove);
        
        // Update order values
        const updatedCategories = newCategories.map((category, index) => ({
            ...category,
            order: index,
        }));

        // Update local state immediately for better UX
        onCategoriesChange(updatedCategories);

        // If journal exists and we have valid category IDs, sync with backend
        if (journalId && updatedCategories.every(cat => cat.id && cat.id > 0)) {
            try {
                const categoryIds = updatedCategories.map(cat => cat.id).filter(id => id > 0);
                await reorderCategoriesMutation.mutateAsync({
                    journalId,
                    categoryIds
                });
            } catch (error) {
                console.error('Failed to reorder categories:', error);
                // Revert to original order on error
                onCategoriesChange(categories);
            }
        }
    }, [categories, onCategoriesChange, journalId, reorderCategoriesMutation]);

    const handleMoveUp = useCallback((index: number) => {
        handleMoveCategory(index, index - 1);
    }, [handleMoveCategory]);

    const handleMoveDown = useCallback((index: number) => {
        handleMoveCategory(index, index + 1);
    }, [handleMoveCategory]);

    const handleAddCategory = (values: { nameFr: string; nameEn: string }) => {
        try {
            if (!journalId) {
                // For new journals, add to local state
                const newCategory: IJournalCategory = {
                    id: 0, // New category
                    journalID: journalId || 0,
                    nameFr: values.nameFr,
                    nameEn: values.nameEn,
                    order: categories.length,
                    questions: [],
                };

                onCategoriesChange([...categories, newCategory]);
                newCategoryForm.reset();
                setShowAddForm(false);
                return;
            }

            // For existing journals, save to backend
            const categoryData = {
                journalID: journalId,
                nameFr: values.nameFr,
                nameEn: values.nameEn,
                order: categories.length,
            };

            addCategoryMutation.mutate({
                journalId: journalId,
                category: categoryData
            }, {
                onSuccess: (categoryId) => {
                    // Add to local state with the returned ID
                    const newCategory: IJournalCategory = {
                        id: categoryId,
                        journalID: journalId,
                        nameFr: values.nameFr,
                        nameEn: values.nameEn,
                        order: categories.length,
                        questions: [],
                    };

                    onCategoriesChange([...categories, newCategory]);
                    newCategoryForm.reset();
                    setShowAddForm(false);
                },
                onError: (error) => {
                    console.error('Failed to add category:', error);
                }
            });
        } catch (error) {
            console.error('Error in handleAddCategory:', error);
        }
    };

    const openDeleteCategoryModal = (index: number) => {
        const categoryToDelete = categories[index];
        const categoryName = categoryToDelete.nameFr || categoryToDelete.nameEn || t('journal.thisCategory');
        const questionCount = categoryToDelete.questions?.length || 0;
        
        modals.openConfirmModal({
            title: t('modal.deleteCategoryModal.title'),
            children: (
                <Stack gap="sm">
                    <Text>{t('modal.deleteCategoryModal.question')}</Text>
                    <Text fw="bold" size="sm" c="dimmed">
                        "{categoryName}"
                    </Text>
                    {questionCount > 0 && (
                        <Text c="orange" fw="bold" size="sm">
                            {t('modal.deleteCategoryModal.warning', { count: questionCount })}
                        </Text>
                    )}
                    <Text fw="bold">{t('modal.deleteCategoryModal.details')}</Text>
                </Stack>
            ),
            labels: { 
                confirm: t('button.delete'), 
                cancel: t('button.cancel') 
            },
            confirmProps: { color: 'red' },
            cancelProps: { variant: 'outline', color: 'gray' },
            onConfirm: () => handleDeleteCategory(index),
            size: 'lg',
            radius: 'xs',
        });
    };

    const handleDeleteCategory = async (index: number) => {
        const categoryToDelete = categories[index];
        
        if (!journalId || !categoryToDelete.id) {
            // For new journals or new categories, just remove from local state
            const newCategories = categories.filter((_, i) => i !== index)
                .map((category, i) => ({ ...category, order: i }));
            onCategoriesChange(newCategories);
            return;
        }

        try {
            // Delete from backend
            await deleteCategoryMutation.mutateAsync(categoryToDelete.id);
            
            // Remove from local state
            const newCategories = categories.filter((_, i) => i !== index)
                .map((category, i) => ({ ...category, order: i }));
            onCategoriesChange(newCategories);
        } catch (error) {
            console.error('Failed to delete category:', error);
            // Could add error handling UI here
        }
    };

    const handleUpdateCategory = async (index: number, updatedCategory: Partial<IJournalCategory>) => {
        const category = categories[index];
        
        // Update local state immediately for better UX
        const newCategories = [...categories];
        newCategories[index] = { ...newCategories[index], ...updatedCategory };
        onCategoriesChange(newCategories);

        // If it's an existing category with an ID, update in backend
        if (journalId && category.id && category.id > 0) {
            try {
                await updateCategoryMutation.mutateAsync({
                    categoryId: category.id,
                    category: {
                        id: category.id,
                        journalID: journalId,
                        nameFr: newCategories[index].nameFr || '',
                        nameEn: newCategories[index].nameEn || '',
                        order: newCategories[index].order,
                    }
                });
            } catch (error) {
                console.error('Failed to update category:', error);
                // Revert local changes on error
                onCategoriesChange(categories);
            }
        }
    };

    const handleQuestionsChange = (categoryIndex: number, questions: IJournalQuestion[]) => {
        const newCategories = [...categories];
        newCategories[categoryIndex] = {
            ...newCategories[categoryIndex],
            questions,
        };
        onCategoriesChange(newCategories);
    };

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text size="lg" fw={600}>
                    {t('journal.categories')}
                </Text>
                <Button
                    leftSection={<IconPlus size={16} />}
                    variant="light"
                    onClick={() => setShowAddForm(!showAddForm)}
                    loading={addCategoryMutation.isPending}
                >
                    {t('journal.addCategory')}
                </Button>
            </Group>

            {showAddForm && (
                <Card withBorder>
                    <Stack gap="sm">
                        <TextInput
                            label={t('formGlobal.nameFr')}
                            placeholder={t('journal.enterCategoryNameFr')}
                            {...newCategoryForm.getInputProps('nameFr')}
                        />
                        <TextInput
                            label={t('formGlobal.nameEn')}
                            placeholder={t('journal.enterCategoryNameEn')}
                            {...newCategoryForm.getInputProps('nameEn')}
                        />

                        <Group justify="flex-end">
                            <Button variant="outline" onClick={() => setShowAddForm(false)}>
                                {t('button.cancel')}
                            </Button>
                            <Button 
                                onClick={() => {
                                    // Manual validation
                                    const validation = newCategoryForm.validate();
                                    if (validation.hasErrors) 
                                        return;
                                    
                                    // Call handler with form values
                                    handleAddCategory(newCategoryForm.values);
                                }}
                                loading={addCategoryMutation.isPending}
                            >
                                {t('button.add')}
                            </Button>
                        </Group>
                    </Stack>
                </Card>
            )}

            {categories.length === 0 && (
                <Alert color="blue" icon={<IconFolderOpen />}>
                    {t('journal.noCategoriesYet')}
                </Alert>
            )}

            {categories.map((category, index) => {
                return (
                    <Card
                        key={category.id || `new-${index}`}
                        withBorder
                        p="sm"
                        style={{
                            backgroundColor: 'var(--mantine-color-gray-1)',
                            borderColor: 'var(--mantine-color-gray-4)',
                            borderWidth: '2px',
                        }}
                    >
                        <Stack gap="md">
                            <Group justify="space-between">
                                <Group gap="xs">
                                    <Group gap={2}>
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            onClick={() => handleMoveUp(index)}
                                            disabled={index === 0}
                                        >
                                            <IconChevronUp size={16} />
                                        </ActionIcon>
                                        <ActionIcon
                                            variant="subtle"
                                            color="gray"
                                            onClick={() => handleMoveDown(index)}
                                            disabled={index === categories.length - 1}
                                        >
                                            <IconChevronDownMove size={16} />
                                        </ActionIcon>
                                    </Group>

                                    <Text fw={500} size="md">
                                        {category.nameFr || category.nameEn}
                                    </Text>
                                    
                                    <Badge size="sm" variant="light">
                                        {category.questions?.length || 0} {t('journal.questions')}
                                    </Badge>
                                </Group>

                                <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    onClick={() => openDeleteCategoryModal(index)}
                                    loading={deleteCategoryMutation.isPending}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Group>

                            <Group grow>
                                <TextInput
                                    label={t('formGlobal.nameFr')}
                                    value={category.nameFr || ''}
                                    onChange={(e) => handleUpdateCategory(index, { nameFr: e.target.value })}
                                    placeholder={t('journal.enterCategoryNameFr')}
                                    name={`category_${index}_nameFr`}
                                />
                                
                                <TextInput
                                    label={t('formGlobal.nameEn')}
                                    value={category.nameEn || ''}
                                    onChange={(e) => handleUpdateCategory(index, { nameEn: e.target.value })}
                                    placeholder={t('journal.enterCategoryNameEn')}
                                    name={`category_${index}_nameEn`}
                                />
                            </Group>

                            {/* Questions management - Always visible */}
                            <Card 
                                withBorder 
                                p="md"
                                style={{
                                    backgroundColor: 'var(--mantine-color-white)',
                                    borderColor: 'var(--mantine-color-gray-3)',
                                }}
                            >
                                <JournalQuestionManager
                                    questions={category.questions || []}
                                    onQuestionsChange={(questions) => handleQuestionsChange(index, questions)}
                                    categoryId={category.id}
                                />
                            </Card>
                        </Stack>
                    </Card>
                );
            })}
        </Stack>
    );
}; 